// NATIVE
import * as React from 'react';

// INSTALLED
import { Grid } from '@mui/material';
import { Formik, Form } from 'formik';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// CUSTOM
import { useDispatch } from 'react-redux';
import { SnackAlert } from '../../SnackAlert';
import { storeEditArea } from '../../../store/areas';
import MultipleSnackbars from '../../MultipleSnackbars';
import { EditarArea } from '../../areas/editar/EditarArea';
import { closeSessionGlobal, hasPemissions } from '../../../commons/utils';
import { ModalSessionExpired } from '../../generics/ModalSessionExpired';
import validationSchema from '../../areas/formModels/editar/validationSchema';
import formInitialValues from '../../areas/formModels/editar/formInitialValues';
import editarAreaFormModel from '../../areas/formModels/editar/editarAreaFormModel';
import { timeToClosedToast } from '../../../commons';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

/**
 * EditarDialogArea component renders a modal dialog for editing an area or department.
 * It uses Formik for form handling and validation, and Redux for state management.
 *
 * @param {object} props - The properties passed to the component.
 * @param {boolean} props.open - Determines if the modal is open or closed.
 * @param {function} props.handleCloseModalEditar - Function to handle closing the modal.
 * @param {boolean} [props.buttons=false] - Optional prop to determine if buttons should be displayed.
 *
 * @returns {JSX.Element} The rendered EditarDialogArea component.
 *
 * @component
 *
 * @example
 * <EditarDialogArea
 *   open={true}
 *   handleCloseModalEditar={handleCloseFunction}
 *   buttons={true}
 * />
 *
 * @remarks
 * This component handles permissions for editing, validates responses, and manages form submission.
 * It also displays various alerts and handles session expiration.
 */
export const EditarDialogArea = (props: any) => {
  // USE DISPATCH
  const dispatch = useDispatch();

  // USE STATE
  const [canEdit, setCanEdit] = React.useState<boolean>(false);
  const [timeAlert, setTimeAlert] = React.useState(timeToClosedToast);
  const [messagesAlerts, setMessagesAlerts] = React.useState<any>([]);

  const [isSubmiting, setIsSubmiting] = React.useState<boolean>(false);

  const [alert_snack, setAlertSnack] = React.useState(false);
  const [modalSessionExpired, setModalSessionExpired] = React.useState<boolean>(false);

  const [txtBtnEditArea, setTxtBtnEditArea] = React.useState<string>('Editar Area');

  const [msgSnack, setMsgSnack] = React.useState({
    msg: '',
    status: 'success',
  });

  // PROPS
  const { open, handleCloseModalEditar, buttons = false, btnSubmiting } = props;

  // DATA MODELS
  const { formId, formField } = editarAreaFormModel;
  const currentValidationSchema = validationSchema[0];

  // USE EFFECT
  React.useEffect(() => {
    checkEdit();
  }, [props]);

  React.useEffect(() => {
    setIsSubmiting(!open);
  }, [props]);

  // CONST FUNCTIONS
  const handleClose = () => {
    handleCloseModalEditar(false);
  };

  const checkEdit = async () => {
    const hasPermissionsEdit = await hasPemissions('modulo.configuracion', 'modulo.areas', 'edit');

    if (hasPermissionsEdit) {
      setCanEdit(true);
    }
  };

  const validateResponse = async (response: any, textActionConcat: string) => {
    if (response.code == 401) {
      await localStorage.removeItem('lastSelectedQypht');
      await closeSessionGlobal();
      await handleCloseModalEditar(false);
      setModalSessionExpired(true);
      return false;
    }

    // 403 NO TIENE PERMISSO PARA REALIZAR ESA ACCION
    if (!response.ok && response.code == 403) {
      const errors: any = {
        msg: response.errors[0] + ' ' + textActionConcat,
        status: 'error',
      };

      await setMsgSnack(errors);
      await setAlertSnack(true);

      return false;
    }

    // 400 ERROR EN DATOS ENVIADOS MAYORMENTE PROPIEDAD INVALIDA O MAL FORMATEADO
    if (!response.ok && response.code == 400) {
      const errors: any = [];

      response.errors.forEach((element: any) => {
        errors.push({
          type: 'error',
          text: element,
        });
      });

      setMessagesAlerts(errors);
      return false;
    }

    return true;
  };

  const _handleSubmit = async (values: any, actions: any) => {
    setTxtBtnEditArea('Un Monento...');
    actions.setSubmitting(true);
    setIsSubmiting(true);
    const response = await dispatch(storeEditArea(values));

    setTxtBtnEditArea('Editar Area');
    const result = await validateResponse(response, ' (Editar Area) ');

    setIsSubmiting(false);

    if (!result) {
      return false;
    }

    if (response.ok) {
      const errors: any = {
        msg: 'Area Editada Con Exito',
        status: 'success',
      };

      await setMsgSnack(errors);
      await setAlertSnack(true);

      actions.setSubmitting(false);
      handleCloseModalEditar(false);
    }
  };

  return (
    <React.Fragment>
      <ModalSessionExpired open={modalSessionExpired} />
      <MultipleSnackbars
        messagesList={messagesAlerts}
        position={'bottom-left'}
        autoClose={timeAlert}
      />
      <SnackAlert
        open={alert_snack}
        onclick={setAlertSnack}
        text={msgSnack.msg}
        severity={msgSnack.status}
      />

      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth={true}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          Editar Area o Departamento
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Grid>
            <Formik
              initialValues={formInitialValues}
              validationSchema={currentValidationSchema}
              onSubmit={_handleSubmit}
            >
              <>
                <Form id={formId}>
                  <EditarArea formField={formField} />

                  {canEdit && (
                    <Button
                      disabled={isSubmiting}
                      style={{ margin: 5, float: 'right' }}
                      type="submit"
                      variant="contained"
                      color={'primary'}
                    >
                      {txtBtnEditArea}
                    </Button>
                  )}
                </Form>
              </>
            </Formik>
          </Grid>
        </DialogContent>
        <DialogActions></DialogActions>
      </BootstrapDialog>
    </React.Fragment>
  );
};
